/**
 * 危险等级与确认强度。
 *
 * 等级语义直接沿用令牌里的状态色语义（不可互换）：
 *   `caution`  需要注意，但可以继续
 *   `warning`  需要立即处理，尚未失控 —— 动作会立刻改变飞行状态
 *   `critical` 不可逆、已越过安全边界
 *
 * 没有 `none`：不需要确认的动作不该调用确认弹层。把"其实不用确认"也塞进来，
 * 只会让"什么时候会弹确认框"变成飞行员猜不透的事 —— 猜不透的确认框等于没有确认框。
 */

export type DangerLevel = 'caution' | 'warning' | 'critical';

export const DANGER_LEVELS: readonly DangerLevel[] = ['caution', 'warning', 'critical'];

export interface DangerPolicy {
  readonly level: DangerLevel;
  /**
   * 确认按钮的视觉权重。
   * 三种都不是"实心危险色"—— 见 `ConfirmDialog` 里关于误触的取舍。
   */
  readonly confirmEmphasis: 'secondary' | 'outlined-warning' | 'outlined-critical';
  /** 后果文案用哪个等级的颜色强调（越危险越不能是中性灰） */
  readonly consequenceTone: DangerLevel;
  /** 是否要求用户逐字输入确认短语（不可逆动作的确认强度） */
  readonly requireConfirmPhrase: boolean;
  /** 是否要求确认按钮写清"将发生什么"
   *（禁止"确定/确认"这类暧昧文案） */
  readonly requireExplicitConfirmLabel: boolean;
  /** 初始焦点永远在"取消"。写成常量是为了让"没有例外"这件事一眼可见 */
  readonly initialFocus: 'cancel';
}

export const DANGER_POLICIES: Readonly<Record<DangerLevel, DangerPolicy>> = {
  caution: {
    level: 'caution',
    confirmEmphasis: 'secondary',
    consequenceTone: 'caution',
    requireConfirmPhrase: false,
    requireExplicitConfirmLabel: false,
    initialFocus: 'cancel',
  },
  warning: {
    level: 'warning',
    confirmEmphasis: 'outlined-warning',
    consequenceTone: 'warning',
    requireConfirmPhrase: false,
    // 会立刻改变飞行状态的动作，"确定/取消"不足以让人知道会发生什么
    requireExplicitConfirmLabel: true,
    initialFocus: 'cancel',
  },
  critical: {
    level: 'critical',
    confirmEmphasis: 'outlined-critical',
    consequenceTone: 'critical',
    // 不可逆 ⇒ 单步确认不够：要逐字输入，把"手滑"和"我确认要这么做"分开
    requireConfirmPhrase: true,
    requireExplicitConfirmLabel: true,
    initialFocus: 'cancel',
  },
};

export function resolveDangerPolicy(level: DangerLevel): DangerPolicy {
  return DANGER_POLICIES[level];
}

/**
 * 暧昧的确认文案：它们描述的是"要不要按"，而不是"按下去会发生什么"。
 * 只在需要 `requireExplicitConfirmLabel` 的等级上拦截。
 */
const AMBIGUOUS_CONFIRM_LABELS: readonly string[] = [
  '确定',
  '确认',
  '好的',
  '好',
  '是',
  'ok',
  'okay',
  'yes',
  'confirm',
  '继续',
  '执行',
];

export function isAmbiguousConfirmLabel(label: string): boolean {
  return AMBIGUOUS_CONFIRM_LABELS.includes(label.trim().toLowerCase());
}

export interface ConfirmPolicyInput {
  readonly danger: DangerLevel;
  readonly consequence: string;
  readonly confirmLabel: string;
  readonly confirmPhrase: string | undefined;
}

/**
 * 策略在渲染期强制，而不是只写在文档里。
 *
 * 配置错误应当在开发时就炸掉：一个"忘了写后果"的确认弹层在生产环境里看起来完全正常，
 * 直到有人在不知道后果的情况下按下了它 —— 那种 bug 没有任何后续信号，只有事故。
 */
export function assertConfirmPolicy(input: ConfirmPolicyInput): DangerPolicy {
  const policy = resolveDangerPolicy(input.danger);
  if (input.consequence.trim() === '') {
    throw new Error(
      '确认弹层必须说清后果：请给出具体后果（例如「将立即让飞机返航，且无法撤销」），而不是「您确定吗？」。',
    );
  }
  if (policy.requireExplicitConfirmLabel && isAmbiguousConfirmLabel(input.confirmLabel)) {
    throw new Error(
      `确认按钮文案必须说明将发生什么（例如「立即返航」）：「${input.confirmLabel}」只说了要不要按，没说会发生什么。`,
    );
  }
  if (policy.requireConfirmPhrase && (input.confirmPhrase === undefined || input.confirmPhrase.trim() === '')) {
    throw new Error(
      '不可逆动作（danger="critical"）必须提供 confirmPhrase：用户需要逐字输入它，才能把「手滑」和「我确认要这么做」分开。',
    );
  }
  return policy;
}

/** 危险等级对应的语义令牌名（组件里据此取色，不写死色值） */
export function dangerColorToken(level: DangerLevel): string {
  return `var(--${level})`;
}

export function dangerSoftToken(level: DangerLevel): string {
  return `var(--${level}-soft)`;
}
